import type {DiffAnchor} from '@github-ui/diffs/types'
import type {PropsWithChildren} from 'react'
import {createContext, use, useMemo} from 'react'

import type {DiffContext} from '../types'

/**
 * Per-file diff metadata that does not change as the user moves between rows
 * within the same file. Hoisted out of `DiffLineContext` so per-row state
 * changes (selection, threads) don't invalidate file-level subscribers.
 */
export type DiffFileContextData = {
  diffEntryId: string
  filePath: string
  fileAnchor: DiffAnchor
  fileLineCount: number
  isSplit: boolean
  diffContext?: DiffContext
  setInGridMode?: (inGridMode: boolean) => void
}

const DiffFileContext = createContext<DiffFileContextData | null>(null)

export function DiffFileContextProvider({
  children,
  diffEntryId,
  filePath,
  fileAnchor,
  fileLineCount,
  isSplit,
  diffContext,
  setInGridMode,
}: PropsWithChildren<DiffFileContextData>) {
  const contextData = useMemo(
    () => ({
      diffEntryId,
      filePath,
      fileAnchor,
      fileLineCount,
      isSplit,
      diffContext,
      setInGridMode,
    }),
    [diffEntryId, filePath, fileAnchor, fileLineCount, isSplit, diffContext, setInGridMode],
  )

  return <DiffFileContext value={contextData}>{children}</DiffFileContext>
}

export function useDiffFileContext(): DiffFileContextData {
  const contextData = use(DiffFileContext)
  if (!contextData) {
    throw new Error('useDiffFileContext must be used within a DiffFileContextProvider')
  }
  return contextData
}
